// Sourcing module — preserves the ProcureFlow RFQ + AI comparison + Award flow, now under KABELUL Sourcing.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowUpRight, BarChart3, Bell, CalendarDays, Check, ChevronRight, CircleDollarSign, Clock3,
  FileCheck2, FileUp, Filter, LayoutDashboard, Menu, MoreHorizontal, PackageCheck, Plus,
  Search, Send, Settings2, ShieldCheck, Sparkles, Users, X,
} from "lucide-react";
import { idr, shortIdr } from "../data/kabelul";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusTone = { "Awaiting decision": "orange", Awarded: "green", Draft: "gray" };

function Badge({ children, tone = "gray" }) {
  return (
    <span data-testid={`status-${String(children).toLowerCase().replaceAll(" ", "-")}`} className={`badge ${tone}`}>
      <span className="badge-dot" />
      {children}
    </span>
  );
}
function Stat({ icon: Icon, label, value, note, accent }) {
  return (
    <div className="stat-card" data-testid={`sourcing-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className={`stat-icon ${accent}`}><Icon size={19} /></div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
    </div>
  );
}

function Overview({ rfq, dashboard, setSub, setSelected }) {
  return (
    <div>
      <div className="k-context-pill" data-testid="business-context-pill"><Sparkles size={11} /> Business Workspace · For Businesses</div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">SOURCING · PROCUREMENT WORKSPACE</p>
          <h1>Business sourcing dashboard</h1>
          <p className="subtitle">Create RFQs, compare vendor quotations with KABELUL AI, and award confidently.</p>
        </div>
        <button data-testid="new-rfq-main-button" className="secondary-button" onClick={() => setSub("rfqs")}>
          <FileUp size={16} /> Review quotations
        </button>
      </motion.div>
      <div className="stats-grid">
        <Stat icon={FileCheck2} label="Active RFQs" value="4" note="+2 from last month" accent="green" />
        <Stat icon={CircleDollarSign} label="Quotations received" value="9" note="Across 4 active RFQs" accent="amber" />
        <Stat icon={Clock3} label="Pending decisions" value="1" note="Needs your review" accent="blue" />
        <Stat icon={ArrowUpRight} label="Estimated savings" value={shortIdr(dashboard?.savings)} note="vs. average quote" accent="red" />
      </div>
      <div className="content-grid">
        <section className="panel showcase-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker"><span className="live-dot" /> SHOWCASE RFQ</div>
              <h2>{rfq?.title}</h2>
              <p className="muted">{rfq?.number} <span className="slash">/</span> {rfq?.project}</p>
            </div>
            <Badge tone={statusTone[rfq?.status] || "orange"}>{rfq?.status}</Badge>
          </div>
          <div className="rfq-highlight">
            <div>
              <span>Decision required</span>
              <b>3 vendor quotations ready to compare</b>
            </div>
            <button data-testid="open-showcase-rfq" className="dark-button" onClick={() => { setSelected(rfq); setSub("comparison"); }}>
              Open comparison <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="mini-metrics">
            <div><small>Budget</small><b>{idr(rfq?.budget)}</b></div>
            <div><small>Best quoted</small><b>{idr(Math.min(...(rfq?.quotations || [{ total: 0 }]).map((q) => q.total)))}</b></div>
            <div><small>Responses</small><b>{rfq?.quotation_count} / {rfq?.vendor_count}</b></div>
            <div><small>Due date</small><b>{rfq?.delivery_date ? new Date(rfq.delivery_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "26 Aug"}</b></div>
          </div>
          <div className="vendor-strip">
            {(rfq?.vendors || []).map((v) => (
              <div className="vendor-chip" key={v.id}>
                <span className={`vendor-avatar ${v.tone}`}>{v.initials}</span>
                <div>
                  <b>{v.name.replace("PT ", "")}</b>
                  <small><Check size={12} /> Quotation received</small>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="panel activity-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker">RECENT ACTIVITY</div>
              <h3>Keep the flow moving</h3>
            </div>
            <button data-testid="activity-more-button" className="quiet-button">View all <ArrowUpRight size={14} /></button>
          </div>
          <div className="activity-list">
            {(dashboard?.activity || []).map((a, i) => (
              <div className="activity-row" key={i}>
                <div className={`activity-icon ${a.icon}`}><Sparkles size={15} /></div>
                <div>
                  <b>{a.label}</b>
                  <p>{a.detail}</p>
                </div>
                <time>{a.time}</time>
              </div>
            ))}
          </div>
          <div className="next-step">
            <div className="next-icon"><ShieldCheck size={19} /></div>
            <div>
              <small>NEXT BEST ACTION</small>
              <b>Review and award RFQ-2026-0142</b>
            </div>
            <button data-testid="next-action-button" onClick={() => { setSelected(rfq); setSub("comparison"); }}>
              <ChevronRight size={18} />
            </button>
          </div>
        </section>
      </div>
      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">YOUR PIPELINE</div>
            <h2>Recent RFQs</h2>
          </div>
          <button data-testid="view-all-rfqs-button" className="quiet-button" onClick={() => setSub("rfqs")}>View all RFQs <ArrowUpRight size={14} /></button>
        </div>
        <div className="rfq-table">
          <div className="table-head"><span>RFQ / PROJECT</span><span>STATUS</span><span>QUOTATIONS</span><span>BUDGET</span><span>UPDATED</span><span /></div>
          {[rfq || {}, { ...rfq, id: "2", number: "RFQ-2026-0138", title: "Panel Listrik Utama — Pabrik Bekasi", status: "In progress", quotation_count: 2, budget: 180000000, project: "PT Karya Industri" }, { ...rfq, id: "3", number: "RFQ-2026-0136", title: "Forklift Electric 2.5T", status: "Awarded", quotation_count: 3, budget: 320000000, project: "PT Cipta Distribusi" }].map((r, i) => (
            <button data-testid={`rfq-row-${i}`} className="table-row" key={`${r.id}-${i}`} onClick={() => { setSelected(r); setSub(i === 0 ? "comparison" : "rfqs"); }}>
              <div>
                <b>{r.title}</b>
                <small>{r.number} <span>•</span> {r.project}</small>
              </div>
              <Badge tone={r.status === "Awarded" ? "green" : r.status === "In progress" ? "blue" : "orange"}>{r.status}</Badge>
              <span className="table-value">{r.quotation_count} received</span>
              <span className="table-value">{idr(r.budget)}</span>
              <span className="table-value muted">{i === 0 ? "12 min ago" : i === 1 ? "2 hours ago" : "Yesterday"}</span>
              <ChevronRight size={17} className="row-arrow" />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function Comparison({ rfq, setSub }) {
  const [awarded, setAwarded] = useState(rfq?.status === "Awarded");
  const [expanded, setExpanded] = useState(null);
  const rec = rfq?.recommendation;
  const quotes = rfq?.quotations || [];
  const award = async () => {
    try {
      await axios.post(`${API}/rfqs/${rfq.id}/award?vendor_id=${rec.recommended_vendor_id}`);
      setAwarded(true);
      toast.success("RFQ awarded to PT Nusantara Solusi Teknik");
    } catch (error) {
      console.warn("Award API unavailable", error);
      toast.error("Award could not be saved. Please try again.");
    }
  };
  return (
    <div className="comparison-page">
      <div className="comparison-top">
        <div>
          <button data-testid="back-to-dashboard" className="back-button" onClick={() => setSub("overview")}>← Back to sourcing</button>
          <div className="title-line">
            <div>
              <p className="eyebrow">{rfq?.number} <span className="slash">/</span> RFQ COMPARISON</p>
              <h1>{rfq?.title}</h1>
              <p className="subtitle">Compare normalized quotations, then make a confident decision.</p>
            </div>
            <Badge tone={awarded ? "green" : "orange"}>{awarded ? "Awarded" : "Awaiting decision"}</Badge>
          </div>
        </div>
        <div className="comparison-actions">
          <button data-testid="export-comparison-button" className="secondary-button" onClick={() => toast.success("Comparison summary prepared")}>Export summary</button>
          {!awarded && <button data-testid="award-vendor-top-button" className="primary-button" onClick={award}><Check size={16} /> Award vendor</button>}
        </div>
      </div>
      <div className="ai-recommendation">
        <div className="ai-orbit"><Sparkles size={21} /></div>
        <div className="recommendation-copy">
          <div className="ai-label">KABELUL AI RECOMMENDATION <span>GPT 5.6 TERRA</span></div>
          <h2>{rec?.headline}: {rec?.recommended_vendor}</h2>
          <p>{rec?.summary}</p>
          <div className="reason-tags">{rec?.why?.slice(0, 3).map((x, i) => <span data-testid={`recommendation-reason-${i}`} key={x}><Check size={13} />{x.split(" — ")[0]}</span>)}</div>
        </div>
        <div className="recommendation-score"><small>OVERALL SCORE</small><strong>96<span>/100</span></strong><b>Best value</b></div>
      </div>
      <div className="comparison-toolbar">
        <div><b>Side-by-side comparison</b><span>3 normalized quotations · updated just now</span></div>
        <div className="legend"><span><i className="green-dot" /> Recommended</span><span><i className="amber-dot" /> Lowest price</span><button data-testid="comparison-filter-button" className="icon-button small" onClick={() => toast.info("All quotations shown")}><Filter size={15} /></button></div>
      </div>
      <div className="comparison-table-wrap">
        <div className="comparison-table">
          <div className="compare-label-col">
            <div className="compare-header"><span>VENDOR QUOTATIONS</span><small>AI-normalized fields</small></div>
            {["Overall score", "Total price", "Technical compliance", "Brand / model", "Specification", "Lead time", "Warranty", "Payment terms", "PPN 11%", "Delivery cost"].map((x, i) => (
              <div className="compare-label" key={x}>{x}{[0, 2, 4].includes(i) && <span className="info-dot">i</span>}</div>
            ))}
          </div>
          {quotes.map((q, i) => (
            <div className={`quote-col ${q.vendor_id === rec?.recommended_vendor_id ? "recommended" : ""}`} key={q.id} data-testid={`quotation-column-${i}`}>
              <div className="compare-header vendor-head">
                <div className={`vendor-avatar ${rfq.vendors?.[i]?.tone || "green"}`}>{rfq.vendors?.[i]?.initials || "VN"}</div>
                <div>
                  <b>{(q.vendor || "Unknown vendor").replace("PT ", "")}</b>
                  <small>{q.source}</small>
                </div>
                {q.vendor_id === rec?.recommended_vendor_id && <span className="recommended-pill"><Sparkles size={12} /> Recommended</span>}
                {q.total === Math.min(...quotes.map((x) => x.total)) && <span className="lowest-pill">Lowest price</span>}
              </div>
              <div className="compare-value score"><strong>{q.overall}</strong><span>/100</span><div className="score-bar"><i style={{ width: `${q.overall}%` }} /></div></div>
              <div className="compare-value price"><b>{idr(q.total)}</b><small>{q.total === Math.min(...quotes.map((x) => x.total)) ? "Rp 59,05 jt below recommendation" : "Within budget"}</small></div>
              <div className={`compare-value compliance ${q.technical === 100 ? "pass" : "warn"}`}><span>{q.technical === 100 ? <Check size={15} /> : <X size={15} />} {q.technical}%</span><small>{q.compliance}</small></div>
              {[`${q.brand}\n${q.model}`, q.specification, q.lead_time, q.warranty, q.payment, idr(q.tax), q.delivery ? idr(q.delivery) : "Included"].map((x, j) => (
                <div className="compare-value" key={j}>{x}</div>
              ))}
              <button data-testid={`why-score-button-${i}`} className="why-button" onClick={() => setExpanded(expanded === q.id ? null : q.id)}>Why this score? <ChevronRight size={14} /></button>
              {expanded === q.id && (
                <div className="score-popover">
                  <b>Score breakdown</b>
                  <span>Technical fit <strong>{Math.round(q.technical * .6)}/60</strong></span>
                  <span>Commercial terms <strong>{Math.round(q.overall - q.technical * .6)}/40</strong></span>
                  <p>{q.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="decision-footer">
        <div>
          <ShieldCheck size={21} />
          <div>
            <b>Ready to approve?</b>
            <span>Decision and reasoning will be saved to the audit trail.</span>
          </div>
        </div>
        {awarded ? (
          <div className="awarded-state"><Check size={17} /> Awarded to {rec?.recommended_vendor}</div>
        ) : (
          <button data-testid="approve-award-button" className="primary-button" onClick={award}><Check size={17} /> Approve & award {rec?.recommended_vendor?.replace("PT ", "")}</button>
        )}
      </div>
    </div>
  );
}

function RfqList({ rfq, setSub, setShowCreate, setSelected }) {
  return (
    <div>
      <div className="page-intro">
        <div>
          <p className="eyebrow">PROCUREMENT PIPELINE</p>
          <h1>RFQs & quotations</h1>
          <p className="subtitle">Every sourcing decision, in one clear view.</p>
        </div>
      </div>
      <div className="filter-row">
        <div className="tabs">
          <button data-testid="rfq-tab-all" className="tab active">All RFQs <span>4</span></button>
          <button data-testid="rfq-tab-pending" className="tab">Needs decision <span>1</span></button>
          <button data-testid="rfq-tab-awarded" className="tab">Awarded <span>2</span></button>
        </div>
        <button data-testid="rfq-filter-button" className="secondary-button"><Filter size={15} /> Filter</button>
      </div>
      <div className="rfq-table list-table">
        <div className="table-head"><span>RFQ / PROJECT</span><span>STATUS</span><span>VENDORS</span><span>QUOTATIONS</span><span>BUDGET</span><span /></div>
        {[rfq || {}, { ...rfq, id: "2", number: "RFQ-2026-0138", title: "Panel Listrik Utama — Pabrik Bekasi", status: "In progress", quotation_count: 2, budget: 180000000, project: "PT Karya Industri" }, { ...rfq, id: "3", number: "RFQ-2026-0136", title: "Forklift Electric 2.5T", status: "Awarded", quotation_count: 3, budget: 320000000, project: "PT Cipta Distribusi" }].map((r, i) => (
          <button data-testid={`rfq-list-row-${i}`} className="table-row" key={i} onClick={() => { setSelected(r); if (i === 0) setSub("comparison"); }}>
            <div>
              <b>{r.title}</b>
              <small>{r.number} <span>•</span> {r.location}</small>
            </div>
            <Badge tone={r.status === "Awarded" ? "green" : r.status === "In progress" ? "blue" : "orange"}>{r.status}</Badge>
            <span className="table-value">{r.vendor_count} invited</span>
            <span className="table-value">{r.quotation_count} received</span>
            <span className="table-value">{idr(r.budget)}</span>
            <ChevronRight size={17} className="row-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function CreateRfqModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", project: "", description: "", delivery_date: "", location: "", budget: "", items: [{ name: "", quantity: 1, brand: "", spec: "" }] });
  const change = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/rfqs`, { ...form, budget: Number(form.budget), items: form.items });
      onCreated(r.data);
      toast.success("RFQ created");
      onClose();
    } catch {
      toast.error("Could not create RFQ");
    }
  };
  return (
    <div className="modal-backdrop">
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="modal" role="dialog">
        <div className="modal-head">
          <div>
            <p className="eyebrow">NEW PROCUREMENT REQUEST</p>
            <h2>Create an RFQ</h2>
            <p className="muted">Start with the requirement. Vendors and quotations come next.</p>
          </div>
          <button data-testid="close-create-rfq" className="icon-button" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label>RFQ title<input data-testid="rfq-title-input" required value={form.title} onChange={(e) => change("title", e.target.value)} placeholder="e.g. Network upgrade — Jakarta office" /></label>
            <label>Project / customer<input data-testid="rfq-project-input" required value={form.project} onChange={(e) => change("project", e.target.value)} placeholder="Company or project name" /></label>
            <label>Required delivery date<input data-testid="rfq-delivery-date-input" required type="date" value={form.delivery_date} onChange={(e) => change("delivery_date", e.target.value)} /></label>
            <label>Delivery location<input data-testid="rfq-location-input" required value={form.location} onChange={(e) => change("location", e.target.value)} placeholder="City, province" /></label>
            <label className="full">Target budget (IDR)<input data-testid="rfq-budget-input" required type="number" value={form.budget} onChange={(e) => change("budget", e.target.value)} placeholder="500000000" /></label>
            <label className="full">Description<textarea data-testid="rfq-description-input" value={form.description} onChange={(e) => change("description", e.target.value)} placeholder="What are you sourcing and why?" /></label>
          </div>
          <div className="line-item-head">
            <b>Line items</b>
            <button data-testid="add-line-item-button" type="button" className="quiet-button" onClick={() => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, brand: "", spec: "" }] })}><Plus size={14} /> Add item</button>
          </div>
          {form.items.map((item, i) => (
            <div className="item-grid" key={i}>
              <input data-testid={`rfq-item-name-${i}`} required placeholder="Item name" value={item.name} onChange={(e) => { const items = [...form.items]; items[i].name = e.target.value; setForm({ ...form, items }); }} />
              <input data-testid={`rfq-item-quantity-${i}`} type="number" placeholder="Qty" value={item.quantity} onChange={(e) => { const items = [...form.items]; items[i].quantity = e.target.value; setForm({ ...form, items }); }} />
              <input data-testid={`rfq-item-spec-${i}`} placeholder="Required specification" value={item.spec} onChange={(e) => { const items = [...form.items]; items[i].spec = e.target.value; setForm({ ...form, items }); }} />
            </div>
          ))}
          <div className="modal-actions">
            <button data-testid="cancel-create-rfq" type="button" className="secondary-button" onClick={onClose}>Cancel</button>
            <button data-testid="submit-create-rfq" className="primary-button" type="submit"><Sparkles size={16} /> Create requirement</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function SourcingPage({ setShowCreate }) {
  const [sub, setSub] = useState("overview");
  const [rfq, setRfq] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard`).then((r) => {
      setDashboard(r.data);
      setRfq(r.data.recent[0]);
    }).catch(() => {
      toast.error("Unable to load sourcing workspace");
    });
  }, []);

  return (
    <div className="page-inner">
      {sub === "overview" && rfq && <Overview rfq={rfq} dashboard={dashboard} setSub={setSub} setSelected={setRfq} />}
      {sub === "comparison" && rfq && <Comparison rfq={rfq} setSub={setSub} />}
      {sub === "rfqs" && (rfq ? (
        <RfqList rfq={rfq} setSub={setSub} setShowCreate={setShowCreate} setSelected={setRfq} />
      ) : (
        <div className="empty-page" data-testid="rfq-list-loading-state">
          <Sparkles size={26} />
          <p>Loading RFQ pipeline…</p>
        </div>
      ))}
      {!rfq && sub === "overview" && (
        <div className="empty-page">
          <Sparkles size={26} />
          <p>Loading sourcing workspace…</p>
        </div>
      )}
    </div>
  );
}
